import { isBefore, startOfDay } from 'date-fns';
import { z } from 'zod';

import { ScheduledBankTransferFrequency } from '__generated__/GQL';
import zodLocales from 'common/zod/locales';

import {
  getTomorrowDate,
  ScheduledBankTransferEndDateDeterminedByOption,
} from '../../../utils';
import locales from './locales';

export const scheduledTransferSettingsFormSchema = z
  .object({
    endDateDeterminedBy: z.nativeEnum(
      ScheduledBankTransferEndDateDeterminedByOption,
    ),
    frequency: z.nativeEnum(ScheduledBankTransferFrequency),
    isAmountAboveThreshold: z.boolean(),
    lastDueDate: z.coerce
      .date()
      .min(
        startOfDay(getTomorrowDate()),
        locales.errors.lastDueDateMustBeInTheFuture,
      )
      .transform((date) => date.toISOString()),
    nextDueDate: z.coerce
      .date()
      .min(
        startOfDay(getTomorrowDate()),
        locales.errors.nextDueDateMustBeInFuture,
      )
      .transform((date) => date.toISOString()),
    numberOfOccurrences: z.coerce.number().int(),
  })
  .superRefine(
    (
      {
        endDateDeterminedBy,
        frequency,
        isAmountAboveThreshold,
        lastDueDate,
        nextDueDate,
        numberOfOccurrences,
      },
      ctx,
    ) => {
      if (
        endDateDeterminedBy ===
          ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate &&
        !lastDueDate
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: zodLocales.errors.required,
          path: ['endDateDeterminedBy'],
        });
      }
      if (
        endDateDeterminedBy ===
          ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate &&
        !!nextDueDate &&
        isBefore(new Date(lastDueDate), new Date(nextDueDate))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locales.errors.lastDueDateMustBeAfterNextDueDate,
          path: ['lastDueDate'],
        });
      }

      if (
        endDateDeterminedBy ===
          ScheduledBankTransferEndDateDeterminedByOption.NumberOfOccurrences &&
        numberOfOccurrences <= 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locales.errors.atLeastOneOccurrence,
          path: ['numberOfOccurrences'],
        });
      }

      if (
        isAmountAboveThreshold &&
        frequency !== ScheduledBankTransferFrequency.Once
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locales.amountAboveThreshold,
          path: ['frequency'],
        });
      }
    },
  );

export type ScheduledTransferSettingsFormSchema = z.infer<
  typeof scheduledTransferSettingsFormSchema
>;
